import { route } from "../../urls";
import Authentication from "./authentication.mdx";
import { MDXSection } from "./snippets";
import { StatusDiagram } from "./status-diagram";

## Authentication

<Authentication token={props.token} />

## Setup

NodeJS supports two module formats [ESM](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Modules) and [CommonJS](https://en.wikipedia.org/wiki/CommonJS). Below details the setup for each environment. After setup, the code is identical regardless of module format.

### ESM

First you’ll need to ensure you have a NodeJS project:

```
npm create esm -y
```

Then install the `replicate` JavaScript library using `npm`:

```
npm install replicate
```

To use the library, first import and create an instance of it:

```jsx
import Replicate from "replicate";

const replicate = new Replicate();
```

This will use the `REPLICATE_API_TOKEN` API token you’ve setup in your environment for authorization.

### CommonJS

First you’ll need to ensure you have a NodeJS project:

```
npm create -y
```

Then install the `replicate` JavaScript library using `npm`:

```
npm install replicate
```

To use the library, first import and create an instance of it:

```jsx
const Replicate = require("replicate");

const replicate = new Replicate();
```

This will use the `REPLICATE_API_TOKEN` API token you’ve setup in your environment for authorization.

## Run the model

Use the `replicate.run()` method to run the model:

```jsx
const input = {{ input }};

const output = await replicate.run("{{ runnableName }}", { input });

{{ printOutput }}
//=> {{ output }}
```

You can learn about pricing for this model on the <a href={route("model_detail", { name: props.version._extras.model.name, username: props.version._extras.model.owner })}>model page</a>.

The `run()` function returns the output directly, which you can then use or pass as the input to another model. If you want to access the full prediction object (not just the output), use the `replicate.predictions.create()` method instead. This will include the prediction id, status, logs, etc.

<MDXSection show={props.hasFileInputs}>

## File inputs

This model accepts files as input. You can provide a file as input using a URL, a local file on your computer, or a base64 encoded object:

### Option 1: Hosted file

Use a URL as in the earlier example:

```js
const {{ fileInputField }} = "{{ fileInputValue }}";
```

This is useful if you already have an image hosted somewhere on the internet.

### Option 2: Local file

You can provide Replicate with a `Blob`, `File` or `Buffer` object and the library will handle the upload for you:

```js
import { readFile } from "node:fs/promises";
const {{ fileInputField }} = await readFile("./path/to/my/{{ fileLocalValue }}");

```

### Option 3: Data URI

You can create a data URI consisting of the base64 encoded data for your file, but this is only recommended if the file is < 1mb

```js
import { readFile } from "node:fs/promises";
const data = (await readFile("./{{ fileLocalValue }}")).toString("base64");
const {{ fileInputField }} = `data:application/octet-stream;base64,${data}`;
```

Then, pass <code>{props.fileInputField}</code> as part of the input:

```js
const input = {{ inputWithFile }};

const output = await replicate.run("{{ runnableName }}", { input });

{{ printOutput }}
//=> {{ output }}
```

</MDXSection>

<MDXSection show={props.streaming !== "none"}>

## Streaming

This model supports streaming. This allows you to receive output as the model is running:

```js
const Replicate = require("replicate")
const replicate = new Replicate()

const input = {{ input }};

for await (const event of replicate.stream("{{ runnableName }}", { input })) {
  // event: { event: string; data: string; id: string }
  {{ printStreamEvent }}
  //=> {{ outputStreamEvent }}
};
process.stdout.write("\n");
```

The `replicate.stream()` method returns a [`ReadableStream`](https://developer.mozilla.org/en-US/docs/Web/API/Streams_API/Using_readable_streams) which can be iterated to transform the events into any data structure needed.

For example, to stream just the output content back:

```js
function handler(request) {
  const stream = new ReadableStream({
    async start(controller) {
      for await (const event of replicate.stream( "{{ runnableName }}", { input })) {
        controller.enqueue(new TextEncoder().encode(`${event}`));
        //=> {{ outputStreamEvent }}
      }
      controller.close();
    },
  });
  return new Response(stream);
}
```

Or, stream a list of JSON objects back to the client instead of server sent events:

```js
function handler(request) {
  const iterator = replicate.stream( "{{ runnableName }}", { input });
  const stream = new ReadableStream({
    async pull(controller) {
      const { value, done } = await iterator.next();
      const encoder = new TextEncoder();

      if (done) {
        controller.close();
      } else if (value.event === "output" && value.data.length > 0) {
        controller.enqueue(encoder.encode(JSON.stringify({ data: value.data }) + "\n"));
      } else {
        controller.enqueue(encoder.encode(""));
      }
    },
  });
  return new Response(stream);
}
```

## Streaming in the browser

The JavaScript library is intended to be run on the server. Once the prediction has been created it's output can be streamed directly from the browser.

The streaming URL uses a standard format called [Server Sent Events](https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events) (or `text/event-stream`) built into all web browsers.

A common implementation is to use a web server to create the prediction using `replicate.predictions.create`, passing the `stream` property set to `true`. Then the `urls.stream` property of the response contains a URL that can be returned to your frontend application:

```js
// POST /run_prediction
handler(req, res) {
  const input = {{ input }};
  const prediction = await replicate.predictions.create({
    {{ runnableFieldName }}: "{{ runnableFieldValue }}",
    input,
    stream: true,
  });
  return Response.json({ url: prediction.urls.stream });
  // Returns {"url": "https://replicate-stream..."}
}
```

Make a request to the server to create the prediction and use the built-in `EventSource` object to read the returned `url`.

```jsx
const response = await fetch("/run_prediction", { method: "POST" });
const { url } = await response.json();

const source = new EventSource(url);
source.addEventListener("output", (evt) => {
  console.log(evt.data) //=> {{ outputStreamEvent }}
});
source.addEventListener("done", (evt) => {
  console.log("stream is complete");
});
```

</MDXSection>

## Prediction lifecycle

Running predictions and trainings can often take significant time to complete, beyond what is reasonable for an HTTP request/response.

When you run a model on Replicate, the prediction is created with a `“starting”` state, then instantly returned. This will then move to `"processing"` and eventual one of `“successful”`, `"failed"` or `"canceled"`.

<StatusDiagram />

You can explore the prediction lifecycle by using the `predictions.get()` method to retrieve the latest version of the prediction until completed.

<details>

<summary>Show example</summary>

<div className="details-inner">

```jsx
const input = {{ input }};
const prediction = await replicate.predictions.create({
  {{ runnableFieldName }}: "{{ runnableFieldValue }}",
  input
});
// { "id": "xyz...", "status": "starting", ... }

const latest = await replicate.predictions.get(prediction.id);
// { "id": "xyz...", "status": "processing", ... }

let completed;
for (let i = 0; i < 5; i++) {
  const latest = await replicate.predictions.get(prediction.id);
  if (latest.status !== "starting" && latest.status !== "processing") {
    completed = latest;
    break;
  }
  // Wait for 2 seconds and then try again.
  await new Promise((resolve) => setTimeout(resolve, 2000));
}

console.log(completed.output);
//=> {{ output }}
```

</div>

</details>

## Webhooks

Webhooks provide real-time updates about your prediction. Specify an endpoint when you [create a prediction](https://replicate.com/docs/reference/http#predictions.create), and Replicate will send HTTP POST requests to that URL when the prediction is created, updated, and finished.

It is possible to provide a URL to the `predictions.create()` function that will be requested by Replicate when the prediction status changes. This is an alternative to polling.

To receive webhooks you’ll need a web server. The following example uses [Hono](https://hono.dev/), a web standards based server, but this pattern applies to most frameworks.

<details>

<summary>Show example</summary>

<div className="details-inner">


```jsx
import { serve } from '@hono/node-server';
import { Hono } from 'hono';

const app = new Hono();
app.get('/webhooks/replicate', async (c) => {
  // Get the prediction from the request.
  const prediction = await c.req.json();
	console.log(prediction);
  //=> {"id": "xyz", "status": "successful", ... }

  // Acknowledge the webhook.
  c.status(200);
  c.json({ok: true});
}));

serve(app, (info) => {
  console.log(`Listening on http://localhost:${info.port}`)
  //=> Listening on http://localhost:3000
});
```

</div>

</details>

Then create the prediction passing in the webhook URL and specify which events you want to receive out of `"start"`, `"output"`, `”logs”` and `"completed"`.

```jsx
const input = {{ input }};

const callbackURL = `https://my.app/webhooks/replicate`;
await replicate.predictions.create({
  {{ runnableFieldName }}: "{{ runnableFieldValue }}",
  input: input,
  webhook: callbackURL,
  webhook_events_filter: ["completed"],
});

// The server will now handle the event and log:
// => {"id": "xyz", "status": "successful", ... }
```

<aside>
ℹ️  The `replicate.run()` method is not used here. Because we're using webhooks, and we don’t need to poll for updates.
</aside>

Co-ordinating between a prediction request and a webhook response will require some glue. A simple implementation for a single JavaScript server could use an event emitter to manage this.

<details>

<summary>Show example</summary>

```jsx
import { EventEmitter } from "node:events";
const webhooks = new EventEmitter();

// In server code, emit the prediction on the event emitter.
app.get('/webhooks/replicate', async (c) => {
  const prediction = await c.req.json();

  // Emit the prediction on the EventEmitter.
  webhooks.emit(prediction.id, prediction)

  // ...
}));

// In request code
await replicate.predictions.create({
  model: "yorickvp/llava-13b",
  version: "a0fdc44e4f2e1f20f2bb4e27846899953ac8e66c5886c5878fa1d6b73ce009e5",
  input: input,
  webhook: callbackURL,
  webhook_events_filter: ["completed"],
});

// Wait for prediction to be emitted on the EventEmitter.
const prediction = await new Promise(resolve => webhooks.addEventListener(prediction.id, resolve));
// {"id": "xyz", "status": "successful", ... }
```

</details>

From a security perspective it is also possible to verify that the webhook came from Replicate. Check out our documentation on [verifying webhooks](https://replicate.com/docs/webhooks#verifying-webhooks) for more information.

## Access a prediction

You may wish to access the prediction object. In these cases it’s easier to use the `replicate.predictions.create()` or `replicate.deployments.predictions.create()` functions which will return the prediction object.

Though note that these functions will only return the created prediction, and it will not wait for that prediction to be completed before returning. Use `replicate.predictions.get()` to fetch the latest prediction.


```jsx
const input = {{ input }};
const prediction = replicate.predictions.create({
  {{ runnableFieldName }}: "{{ runnableFieldValue }}",
  input
});
// { "id": "xyz123", "status": "starting", ... }
```

## Cancel a prediction

You may need to cancel a prediction. Perhaps the user has navigated away from the browser or canceled your application. To prevent unnecessary work and reduce runtime costs you can use the `replicate.predictions.cancel` function and pass it a prediction id.

```javascript
await replicate.predictions.cancel(prediction.id);
```
